import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import adminApi from '../../../utils/admin.api';
import RequestTable from './RequestTable';
import ReviewDialog from './ReviewDialog';
import EditAccessDialog from './EditAccessDialog';

const RequestManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // First-time review
  const [reviewTarget, setReviewTarget] = useState(null);

  // Post-review access modification
  const [editTarget, setEditTarget] = useState(null);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Read ─────────────────────────────────────────────────────────────────────

  const loadRequests = useCallback(() => {
    setLoading(true);
    adminApi
      .getRequests(page + 1, rowsPerPage, filter)
      .then(({ data }) => {
        setRequests(Array.isArray(data?.data?.items) ? data.data.items : []);
        setTotal(data?.data?.total ?? 0);
      })
      .catch(() => setError('Failed to load requests.'))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, filter]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(0);
  };

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleReviewDone = useCallback(() => {
    loadRequests();
    showSnack('Request reviewed successfully.');
  }, [loadRequests]);

  const handleEditDone = useCallback(() => {
    loadRequests();
    showSnack('Access updated successfully.');
  }, [loadRequests]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Access Requests
      </Typography>

      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography color="error">{error}</Typography>
          <Button size="small" onClick={loadRequests}>Retry</Button>
        </Box>
      )}

      <RequestTable
        requests={requests}
        loading={loading}
        filter={filter}
        onFilterChange={handleFilterChange}
        onReview={(req) => setReviewTarget(req)}
        onEditAccess={(req) => setEditTarget(req)}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      {/* First-time review */}
      <ReviewDialog
        open={Boolean(reviewTarget)}
        onClose={() => setReviewTarget(null)}
        requestSummary={reviewTarget}
        onDone={handleReviewDone}
      />

      {/* Post-review access modification */}
      <EditAccessDialog
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        requestSummary={editTarget}
        onDone={handleEditDone}
      />

      {/* Success / Error toast */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestManagement;
